import { createContext, RefObject, useContext } from "react";
import { ToolbarItemProps, ToolbarOverflowTypes, ToolbarSize } from "../types";

/**
 * Context for sharing toolbar state with child components
 */
export type ToolbarContextProps = {
  /**
   * Array of toolbar items that should appear in the overflow menu
   */
  overflowItems: ToolbarItemProps[];
  /**
   * Function to add an item to the overflow menu
   */
  addItem: (element: ToolbarItemProps) => void;
  /**
   * Function to remove an item from the overflow menu
   */
  removeItem: (element: ToolbarItemProps) => void;
  /**
   * Reference to the toolbar container element
   */
  toolbarRef: RefObject<HTMLDivElement>;
  /**
   * Array of item IDs in their original order
   */
  orderedIds?: string[];
  /**
   * Overflow behavior type (wrap or collapse)
   */
  overflow: ToolbarOverflowTypes;
  /**
   * Size of toolbar child components
   */
  size: ToolbarSize;
};

/**
 * Context for managing toolbar state including overflow and size
 */
export const ToolbarContext = createContext<ToolbarContextProps>({
  overflowItems: [],
  addItem: () => {
    return;
  },
  removeItem: () => {
    return;
  },
  toolbarRef: { current: null },
  orderedIds: [],
  overflow: "wrap",
  size: "xsmall",
});

/**
 * Hook to access the toolbar size from context
 * @returns The current toolbar size
 */
export const useToolbarSize = (): ToolbarSize =>
  useContext(ToolbarContext).size;
